export const APP_PAGE_KEYS = Object.freeze({
  home: "home",
  schedules: "schedules",
  products: "products",
  themes: "themes",
  mypage: "mypage",
});

export const SUPPORTED_APP_SCHEMA_VERSION = "1";
export const SUPPORTED_COMPONENT_VERSION = "1";

export const NAVIGATION_ACTION_CODES = Object.freeze({
  home: "tab.home",
  schedules: "tab.schedules",
  products: "tab.products",
  themes: "tab.themes",
  mypage: "tab.mypage",
});

export const NAVIGATION_ROUTES = Object.freeze({
  [NAVIGATION_ACTION_CODES.home]: "/tabs/home",
  [NAVIGATION_ACTION_CODES.schedules]: "/tabs/schedules",
  [NAVIGATION_ACTION_CODES.products]: "/tabs/products",
  [NAVIGATION_ACTION_CODES.themes]: "/tabs/themes",
  [NAVIGATION_ACTION_CODES.mypage]: "/tabs/mypage",
});

export const DEFAULT_NAVIGATION = Object.freeze([
  Object.freeze({ key: "home", label: "홈", actionCode: NAVIGATION_ACTION_CODES.home, iconKey: "home", sortOrder: 10, enabled: true, isRequired: true }),
  Object.freeze({ key: "schedules", label: "촬영 일정", actionCode: NAVIGATION_ACTION_CODES.schedules, iconKey: "schedule", sortOrder: 20, enabled: true, isRequired: false }),
  Object.freeze({ key: "products", label: "제품 신청", actionCode: NAVIGATION_ACTION_CODES.products, iconKey: "product", sortOrder: 30, enabled: true, isRequired: false }),
  Object.freeze({ key: "themes", label: "촬영 테마", actionCode: NAVIGATION_ACTION_CODES.themes, iconKey: "theme", sortOrder: 40, enabled: true, isRequired: false }),
  Object.freeze({ key: "mypage", label: "마이페이지", actionCode: NAVIGATION_ACTION_CODES.mypage, iconKey: "mypage", sortOrder: 50, enabled: true, isRequired: false }),
]);

const nativeSection = (key, sortOrder, title, description = "") => Object.freeze({
  key,
  type: "native",
  componentVersion: "1",
  variant: "default",
  sortOrder,
  enabled: true,
  props: Object.freeze({ title, description }),
  dataSourceKey: null,
  minClientVersion: null,
});

export const DEFAULT_PAGE_CONFIGS = Object.freeze({
  [APP_PAGE_KEYS.home]: Object.freeze({
    key: APP_PAGE_KEYS.home,
    name: "홈",
    title: "홈",
    route: "/tabs/home",
    enabled: true,
    isRequired: true,
    sections: Object.freeze([
      nativeSection("welcome", 10, "환영합니다!", "우리 가족의 이야기를 함께 만들어가요\n스케줄이 배정되면 이곳에서\n안내해드릴게요."),
      nativeSection("today_tasks", 20, "오늘 할 일"),
      nativeSection("recent_shooting", 30, "최근 촬영사진"),
      nativeSection("upcoming_schedule", 40, "예정된 촬영일정"),
      nativeSection("events", 60, "이벤트 & 프로모션", "내 지점과 촬영 일정에 맞는 혜택을 확인해보세요."),
      nativeSection("review_gallery", 70, "스튜디오 갤러리", "스튜디오가 촬영분류별로 등록한 사진을 만나보세요."),
      nativeSection("popular_themes", 80, "인기 촬영 테마", "고객님들이 많이 찾는 촬영 테마를 확인해보세요."),
      nativeSection("notice", 90, "공지 · 문의", "중요한 소식과 안내를 한눈에 확인하세요."),
    ]),
  }),
  [APP_PAGE_KEYS.schedules]: Object.freeze({
    key: APP_PAGE_KEYS.schedules,
    name: "촬영 일정",
    title: "촬영 일정",
    route: "/tabs/schedules",
    enabled: true,
    isRequired: false,
    sections: Object.freeze([
      nativeSection("dashboard", 10, "촬영 진행 현황"),
      nativeSection("filters", 20, ""),
      nativeSection("schedule_list", 30, ""),
    ]),
  }),
  [APP_PAGE_KEYS.products]: Object.freeze({
    key: APP_PAGE_KEYS.products,
    name: "제품 신청",
    title: "제품 신청",
    route: "/tabs/products",
    enabled: true,
    isRequired: false,
    sections: Object.freeze([
      nativeSection("management", 10, "제품 제작 관리", "계약한 상품의 제작 진행 상태를 확인해보세요."),
      nativeSection("product_list", 20, "전체 계약 상품"),
    ]),
  }),
  [APP_PAGE_KEYS.themes]: Object.freeze({
    key: APP_PAGE_KEYS.themes,
    name: "촬영 테마",
    title: "촬영 테마",
    route: "/tabs/themes",
    enabled: true,
    isRequired: false,
    sections: Object.freeze([
      nativeSection("selection", 10, "촬영테마 선택중"),
      nativeSection("filters", 20, ""),
      nativeSection("theme_grid", 30, ""),
    ]),
  }),
  [APP_PAGE_KEYS.mypage]: Object.freeze({
    key: APP_PAGE_KEYS.mypage,
    name: "마이페이지",
    title: "마이페이지",
    route: "/tabs/mypage",
    enabled: true,
    isRequired: false,
    sections: Object.freeze([
      nativeSection("profile", 10, ""),
      nativeSection("today_tasks", 20, "나의 활동"),
      nativeSection("photos", 30, "나의 사진", "스튜디오에서 받은 사진을 촬영별로 모아보세요."),
      nativeSection("studio", 40, "연결 스튜디오"),
      nativeSection("contract_payment", 50, "계약 · 결제"),
      nativeSection("shipping", 60, "배송 정보"),
      nativeSection("account_settings", 70, "계정 및 설정"),
    ]),
  }),
});

export const HOME_GENERIC_SECTION_TYPES = Object.freeze(["rich_text", "banner", "content_list"]);

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
}

function toBoolean(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "y", "yes", "enabled", "visible"].includes(normalized)) return true;
    if (["false", "0", "n", "no", "disabled", "hidden"].includes(normalized)) return false;
  }
  return fallback;
}

function toSortOrder(value, fallback) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
}

function extractResult(payload) {
  return payload?.result || payload?.data?.result || payload?.data || payload || {};
}

function isDatabaseSource(value) {
  return String(value || "").trim().toLowerCase() === "database";
}

function isSupportedSchemaVersion(value) {
  return String(value || "").trim() === SUPPORTED_APP_SCHEMA_VERSION;
}

function isSupportedComponentVersion(value) {
  return String(value || "").trim() === SUPPORTED_COMPONENT_VERSION;
}

function normalizeNavigationItem(item, index, { authoritative = false } = {}) {
  const actionCode = String(firstValue(item?.actionCode, item?.action_code, item?.action, item?.key) || "").trim();
  const defaultItem = DEFAULT_NAVIGATION.find((candidate) => candidate.actionCode === actionCode || candidate.key === actionCode);

  if (!defaultItem) return null;

  return {
    ...defaultItem,
    ...item,
    key: String(firstValue(item?.key, defaultItem.key)),
    label: String(firstValue(item?.label, item?.title, defaultItem.label)),
    actionCode: defaultItem.actionCode,
    iconKey: String(firstValue(item?.iconKey, item?.icon_key, defaultItem.iconKey)),
    sortOrder: toSortOrder(firstValue(item?.sortOrder, item?.sort_order, item?.order), defaultItem.sortOrder + index),
    enabled: defaultItem.isRequired
      ? true
      : toBoolean(
        firstValue(item?.enabled, item?.visible, item?.isVisible, item?.is_visible),
        authoritative ? false : defaultItem.enabled,
      ),
    isRequired: defaultItem.isRequired,
    path: NAVIGATION_ROUTES[defaultItem.actionCode],
  };
}

export function normalizeAppManifest(payload) {
  const result = extractResult(payload);
  const schemaVersion = String(firstValue(
    result?.schemaVersion,
    result?.schema_version,
    SUPPORTED_APP_SCHEMA_VERSION,
  ));
  const source = String(firstValue(
    result?.source,
    Array.isArray(result?.navigation) && result.navigation.length > 0 ? "remote" : "fallback",
  ));
  if (isDatabaseSource(source) && !isSupportedSchemaVersion(schemaVersion)) {
    return normalizeAppManifest(null);
  }
  const authoritative = isDatabaseSource(source);
  const remoteNavigation = Array.isArray(result?.navigation)
    ? result.navigation
    : Array.isArray(result?.tabs)
      ? result.tabs
      : Array.isArray(result?.items)
        ? result.items
        : [];
  const normalizedRemoteNavigation = remoteNavigation
    .map((item, index) => normalizeNavigationItem(item, index, { authoritative }))
    .filter(Boolean);
  const remoteByAction = new Map(
    normalizedRemoteNavigation.map((item) => [item.actionCode, item]),
  );
  const requiredHome = remoteByAction.get(NAVIGATION_ACTION_CODES.home) || {
    ...DEFAULT_NAVIGATION.find((item) => item.actionCode === NAVIGATION_ACTION_CODES.home),
    path: NAVIGATION_ROUTES[NAVIGATION_ACTION_CODES.home],
  };
  const navigationSource = authoritative
    ? [
      ...normalizedRemoteNavigation.filter((item) => item.actionCode !== NAVIGATION_ACTION_CODES.home),
      { ...requiredHome, enabled: true, isRequired: true },
    ]
    : DEFAULT_NAVIGATION.map((defaultItem) => {
      const remote = remoteByAction.get(defaultItem.actionCode);
      return remote || {
        ...defaultItem,
        path: NAVIGATION_ROUTES[defaultItem.actionCode],
      };
    });
  const navigation = navigationSource
    .filter((item) => item.enabled || item.isRequired)
    .sort((left, right) => left.sortOrder - right.sortOrder);

  return {
    schemaVersion,
    source,
    release: result?.release || null,
    navigation,
    pages: Array.isArray(result?.pages) ? result.pages : [],
  };
}

function normalizeRemoteSection(section, index, defaultSection, { authoritative = false } = {}) {
  const props = section?.props && typeof section.props === "object" && !Array.isArray(section.props)
    ? section.props
    : {};
  const type = String(firstValue(section?.type, defaultSection?.type, "native")).trim().toLowerCase();

  return {
    ...(defaultSection || {}),
    ...section,
    key: String(firstValue(section?.key, section?.sectionKey, section?.section_key, defaultSection?.key, `remote-${index}`)),
    type,
    componentVersion: String(firstValue(section?.componentVersion, section?.component_version, defaultSection?.componentVersion, "1")),
    variant: String(firstValue(section?.variant, defaultSection?.variant, "default")),
    sortOrder: toSortOrder(firstValue(section?.sortOrder, section?.sort_order, section?.order), defaultSection?.sortOrder ?? ((index + 1) * 10)),
    enabled: toBoolean(
      firstValue(section?.enabled, section?.visible, section?.isVisible, section?.is_visible),
      authoritative ? false : (defaultSection?.enabled ?? true),
    ),
    props: {
      ...(defaultSection?.props || {}),
      ...props,
    },
    dataSourceKey: firstValue(section?.dataSourceKey, section?.data_source_key, defaultSection?.dataSourceKey, null),
    minClientVersion: firstValue(section?.minClientVersion, section?.min_client_version, defaultSection?.minClientVersion, null),
  };
}

export function normalizePageConfig(pageKey, payload) {
  const defaultPage = DEFAULT_PAGE_CONFIGS[pageKey] || DEFAULT_PAGE_CONFIGS[APP_PAGE_KEYS.home];
  const result = extractResult(payload);
  const schemaVersion = String(firstValue(
    result?.schemaVersion,
    result?.schema_version,
    SUPPORTED_APP_SCHEMA_VERSION,
  ));
  const source = String(firstValue(
    result?.source,
    Array.isArray(result?.sections) && result.sections.length > 0 ? "remote" : "fallback",
  ));
  if (isDatabaseSource(source) && !isSupportedSchemaVersion(schemaVersion)) {
    return normalizePageConfig(pageKey, null);
  }
  const authoritative = isDatabaseSource(source);
  const hasRemotePage = Boolean(
    result?.page
    && typeof result.page === "object"
    && !Array.isArray(result.page),
  );
  const remotePage = result?.page && typeof result.page === "object" ? result.page : {};
  const remoteSections = Array.isArray(result?.sections)
    ? result.sections
    : Array.isArray(remotePage?.sections)
      ? remotePage.sections
      : [];
  const defaultByKey = new Map(defaultPage.sections.map((section) => [section.key, section]));
  const remoteKnownByKey = new Map();
  const unsupportedRemoteKeys = new Set();
  const homeGenericSections = [];

  remoteSections.forEach((section, index) => {
    const key = String(firstValue(section?.key, section?.sectionKey, section?.section_key, ""));
    const defaultSection = defaultByKey.get(key);

    if (defaultSection) {
      const normalizedSection = normalizeRemoteSection(
        section,
        index,
        defaultSection,
        { authoritative },
      );
      if (!isSupportedComponentVersion(normalizedSection.componentVersion)) {
        unsupportedRemoteKeys.add(key);
        return;
      }
      if (normalizedSection.type === defaultSection.type) {
        remoteKnownByKey.set(key, normalizedSection);
      }
      return;
    }

    const genericSection = normalizeRemoteSection(
      section,
      index,
      null,
      { authoritative },
    );
    if (!isSupportedComponentVersion(genericSection.componentVersion)) {
      return;
    }
    if (
      pageKey === APP_PAGE_KEYS.home
      && HOME_GENERIC_SECTION_TYPES.includes(genericSection.type)
    ) {
      homeGenericSections.push(genericSection);
    }
  });

  const sections = [
    ...(authoritative
      ? [...remoteKnownByKey.values()]
      : defaultPage.sections
        .filter((section) => !unsupportedRemoteKeys.has(section.key))
        .map((section) => remoteKnownByKey.get(section.key) || { ...section })),
    ...homeGenericSections,
  ].sort((left, right) => left.sortOrder - right.sortOrder);

  return {
    schemaVersion,
    source,
    release: result?.release || null,
    page: {
      ...defaultPage,
      ...remotePage,
      key: pageKey,
      title: String(firstValue(remotePage?.title, defaultPage.title)),
      enabled: defaultPage.isRequired
        ? true
        : authoritative && !hasRemotePage
          ? false
          : toBoolean(
            firstValue(remotePage?.enabled, remotePage?.visible),
            authoritative ? false : defaultPage.enabled,
          ),
      isRequired: defaultPage.isRequired,
    },
    sections,
  };
}

export function getVisibleSections(pageConfig) {
  return (Array.isArray(pageConfig?.sections) ? pageConfig.sections : [])
    .filter((section) => section?.enabled !== false)
    .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));
}

export function getSectionProps(section, fallback = {}) {
  return {
    ...fallback,
    ...(section?.props && typeof section.props === "object" ? section.props : {}),
  };
}
